<script lang="ts" setup>
import Modal from '~/components/Common/Modal/Modal.vue'
import Select from '~/components/Common/Form/Select.vue'
import Button from '~/components/Common/Button/Button.vue'
import { ResourceType, AlertType } from '~/types/common'
import { type UserReport, ReportReason } from '~/types/profile'
import TiptapEditor from '~/components/TiptapEditor.vue'
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import * as zod from 'zod'
import { useProfileSelfStore } from '~/stores/user/Self'

const props = defineProps<{
	type: ResourceType
	show: boolean
	data: Partial<UserReport>
}>()

const emits = defineEmits(['close', 'complete'])

const isLoading = ref(false)
const error = ref('')

let type = ''

switch (props.type) {
	case ResourceType.FORUM_POST:
		type = 'discussion'
		break
	case ResourceType.FORUM_POST_COMMENT:
	case ResourceType.MAGAZINE_COMMENT:
		type = 'comment'
		break
}

const validationSchema = toTypedSchema(
	zod.object({
		reason: zod.string().min(1, { message: 'Please provide a reason for reporting this.' }),
		content: zod.string().min(30, { message: 'Too short, add more detail to your comment.' }),
	}),
)

const { handleSubmit, errors } = useForm({
	validationSchema,
})

const { value: reason } = useField('reason', { value: '' })
const { value: content } = useField('content', { value: '' })

function contentUpdated(value: string) {
	content.value = value
}

const submit = handleSubmit(async values => {
	error.value = ''
	isLoading.value = true

	try {
		const data = {
			reason: values.reason as ReportReason,
			description: values.content,
			resource_type: props.type,
			...props.data,
		}

		const userStore = useProfileSelfStore()
		const response = await userStore().report(data)

		if (!response.id) {
			error.value = `Error reporting ${type}. Please try again.`
		} else {
			emits('complete')
		}
	} catch (err: any) {
		console.error(err)
		error.value = `Error reporting ${type}. Please try again.`
	} finally {
		isLoading.value = false
	}
})
</script>
<template>
	<Modal
		:show="show"
		@close="emits('close')"
		:title="`Report ${ type }`"
	>

			<div class="text-sm text-gray-500">Please provide a reason for reporting this {{ type }}.</div>

			<Select
				v-model="reason"
				name="reason"
				:options="[{
					value: ReportReason.ABUSE,
					label: 'Bullying, harassment or abuse',
				}, {
					value: ReportReason.HATE,
					label: 'Violent, hateful or disturbing content',
				}, {
					value: ReportReason.SELLING,
					label: 'Selling or promoting',
				}, {
					value: ReportReason.ADULT,
					label: 'Adult content',
				}, {
					value: ReportReason.SCAM,
					label: 'Scam, fraud or sale information',
				}, {
					value: ReportReason.SPAM,
					label: 'Spam',
				}, {
					value: ReportReason.OTHER,
					label: 'Other',
				}]"
				optionsDisplay="label"
				optionsKey="value"
			/>


			<span class="text-red-700 text-sm">{{ errors.reason }}</span>

			<TiptapEditor
				id="content"
				v-model="content"
				hide-toolbar
				:content="(content as string) ?? ''"
				name="content"
				class="mt-2"
				@input="contentUpdated" />

			<span class="text-red-700 text-sm">{{ errors.content }}</span>

			<div>
				<Button :disabled="isLoading" class="pt-2" @click="submit">{{
					isLoading ? `Reporting ${type}...` : `Report ${type}`
				}}</Button>
			</div>

			<Alert v-if="error" :type="AlertType.Error" class="slideInUp mt-4 text-center" :html="error"></Alert>
		</Modal>
</template>
